import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { updateDocumentWorkflowTemplateProperties } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	getBackendErrorMessage,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useMasterDocumentsEndpoints } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsEndpoints.ts';
import { buildSignatureSlotsQueryKey } from './useSignatureSlotsQuery.ts';

/**
 * Writes only `requiresPostDating`. Deliberately does not invalidate the template's `byId` query:
 * its refetch would `form.reset` the drawer and drop unsaved edits.
 */
export const useSetPostDating = () => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const queryClient = useQueryClient();
	const { list: listKey, byId } = useMasterDocumentsEndpoints();
	// Signature slots are deal-scoped, so only a programme edit has a key to invalidate.
	const { dealId, mipId } = useParams();

	return useMutation({
		mutationFn: async (params: {
			templateId: string;
			requiresPostDating: boolean;
		}): Promise<void> => {
			await updateDocumentWorkflowTemplateProperties({
				endpoint: byId(params.templateId),
				requiresPostDating: params.requiresPostDating,
			});
		},
		onSuccess: async (_result, { templateId }) => {
			await queryClient.invalidateQueries({ queryKey: [listKey] });
			if (dealId && mipId) {
				// `useDocxDateAnchors` reads the toggle from this query.
				await queryClient.invalidateQueries({
					queryKey: [
						buildSignatureSlotsQueryKey({ dealId, programmeId: mipId, templateId }),
					],
				});
			}
			showToastV2({ title: tMipManage('master_documents.toast.updated') });
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			// These refusals carry no `code` (only a `BusinessException` gets one), so show the
			// server's message.
			const backendMessage = getBackendErrorMessage(error);
			showToastV2({
				state: 'error',
				title:
					typeof backendMessage === 'string' && backendMessage.length > 0
						? backendMessage
						: tDefault('common.toast.error.message'),
			});
		},
	});
};
